import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import { Trash2 } from "lucide-react";
import MobileViewContainer from "@/Components/MobileViewContainer";
import { Button, Container } from "@/Components/Common/Tailwind";
import NavBottomBar from "@/Components/Common/NavBottomBar";
import QuantityButtonInput from "@/Components/Common/QuantityButtonInput";
import LoginPromptModal from "@/Components/modal/LoginPromptModal";
import { useAuthContext } from "@/Components/AuthContext";
import { tableOrderService } from "@/api/services/table-order.service";

const orderTypeLabels = {
  serving: "서빙",
  pickup: "픽업",
};

const STEP_MENU = 1;
const STEP_ORDER_TYPE = 2;
const STEP_TABLE = 3;
const STEP_REVIEW = 4;

const steps = [
  { id: STEP_MENU, title: "메뉴 선택" },
  { id: STEP_ORDER_TYPE, title: "주문 타입" },
  { id: STEP_TABLE, title: "테이블 선택" },
  { id: STEP_REVIEW, title: "주문 확인" },
];

const formatTableSummary = (table) => {
  if (!table) return "테이블 미지정";
  return table.name || "테이블 미지정";
};

const formatGroupRule = (group) => {
  const required = group.is_required || group.min_select > 0;
  const prefix = required ? "필수 선택" : "선택 사항";
  const parts = [];
  if (group.min_select > 1) parts.push(`최소 ${group.min_select}개`);
  if (group.max_select > 1) parts.push(`최대 ${group.max_select}개`);
  return parts.length > 0 ? `${prefix} · ${parts.join(", ")}` : prefix;
};

const formatOrderTableSummary = (order) => {
  if (order?.table_name && order?.table_code) {
    return `${order.table_name} (${order.table_code})`;
  }

  if (order?.order_type === "pickup") {
    return "픽업 주문";
  }

  return "테이블 미지정";
};

const CustomerOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthContext();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [tableGroups, setTableGroups] = useState([]);
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);

  const [currentStep, setCurrentStep] = useState(STEP_MENU);
  const [isTableLockedByQr, setIsTableLockedByQr] = useState(false);
  const [selectedTableGroupId, setSelectedTableGroupId] = useState("");
  const [selectedTableId, setSelectedTableId] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTableCodeInput, setShowTableCodeInput] = useState(false);
  const [tableCodeInput, setTableCodeInput] = useState("");
  const [tableLookupError, setTableLookupError] = useState("");

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [memo, setMemo] = useState("");
  const [orderType, setOrderType] = useState("");

  const [scrolled, setScrolled] = useState(false);
  const [menuPicker, setMenuPicker] = useState(null);
  const [menuPickerOptions, setMenuPickerOptions] = useState({});
  const [menuPickerQuantity, setMenuPickerQuantity] = useState(1);
  const [isCartExpanded, setIsCartExpanded] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);

  const sectionRefs = React.useRef({});

  const tableCodeParam = searchParams.get("table_code");
  const tableIdParam = searchParams.get("table_id");


  const filteredTables = useMemo(() => {
    const targetGroupId = selectedTableGroupId ? Number(selectedTableGroupId) : null;
    return tables.filter((table) => {
      if (targetGroupId && table.group !== targetGroupId) return false;
      return true;
    });
  }, [selectedTableGroupId, tables]);

  const groupedMenus = useMemo(() => {
    return categories
      .map((cat) => ({
        category: cat,
        menus: menus.filter((m) => m.is_active && m.is_web_visible && m.category === cat.id),
      }))
      .filter((g) => g.menus.length > 0);
  }, [categories, menus]);

  const cartTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.line_amount, 0),
    [cartItems]
  );

  const effectiveStep = useMemo(() => {
    if (orderType === "pickup" && currentStep === STEP_REVIEW) {
      return STEP_REVIEW;
    }
    return currentStep;
  }, [currentStep, orderType]);

  const ensureLoggedIn = useCallback(() => {
    if (accessToken) return true;
    setShowLoginModal(true);
    return false;
  }, [accessToken]);

  const toSubmitItems = useCallback((items) => {
    return items.map((item) => ({
      menu_id: item.menu_id,
      quantity: item.quantity,
      options: (item.options || []).map((option) => ({
        menu_option_id: option.menu_option_id,
        quantity: option.quantity || 1,
      })),
    }));
  }, []);

  const buildCartItem = useCallback((menu, quantity = 1, selectedOptions = []) => {
    const optionTotal = selectedOptions.reduce(
      (sum, option) => sum + option.extra_price_snapshot * (option.quantity || 1),
      0
    );

    return {
      id: `${Date.now()}-${Math.random()}`,
      menu_id: menu.id,
      menu_name_snapshot: menu.name,
      image: menu.image || null,
      quantity,
      unit_price_snapshot: menu.price,
      line_amount: (menu.price + optionTotal) * quantity,
      options: selectedOptions,
    };
  }, []);

  const appendCartItem = useCallback((item) => {
    setOrderComplete(null);
    setCartItems((prev) => [...prev, item]);
    setIsCartExpanded(true);
  }, []);

  const loadBaseData = useCallback(async () => {
    setLoading(true);
    try {
      const [tableGroupRes, tableRes, categoryRes, menuRes] = await Promise.all([
        tableOrderService.listAllTableGroups({ is_active: true }),
        tableOrderService.listAllServiceTables({ is_active: true }),
        tableOrderService.listAllCategories({
          is_active: true,
          visibility_target: "web",
        }),
        tableOrderService.listAllMenus({
          is_active: true,
          visibility_target: "web",
        }),
      ]);

      const tableData = tableRes.results || [];
      const visibleMenus = (menuRes.results || []).filter(
        (menu) => menu.is_active && menu.is_web_visible,
      );
      const visibleCategoryIds = new Set(
        visibleMenus.map((menu) => menu.category),
      );
      const visibleCategories = (categoryRes.results || []).filter(
        (item) =>
          item.is_active &&
          item.is_web_visible &&
          visibleCategoryIds.has(item.id),
      );

      setTableGroups((tableGroupRes.results || []).filter((item) => item.is_active));
      setTables(tableData);
      setCategories(visibleCategories);
      setMenus(visibleMenus);

      if (tableCodeParam) {
        setTableCodeInput(tableCodeParam);
      }

      let qrMatchedTable = null;
      if (tableIdParam) {
        qrMatchedTable = tableData.find((table) => String(table.id) === String(tableIdParam));
      } else if (tableCodeParam) {
        const normalizedCode = tableCodeParam.trim().toLowerCase();
        qrMatchedTable = tableData.find((table) => table.code?.toLowerCase() === normalizedCode);
      }

      if (qrMatchedTable) {
        setSelectedTableGroupId(qrMatchedTable.group ? String(qrMatchedTable.group) : "");
        setSelectedTableId(String(qrMatchedTable.id));
        setSelectedTable(qrMatchedTable);
        setTableCodeInput(qrMatchedTable.code || "");
        setIsTableLockedByQr(Boolean(tableCodeParam || tableIdParam));
        setTableLookupError("");
      } else if (tableCodeParam || tableIdParam) {
        setTableLookupError("유효하지 않은 테이블 QR 입니다. 직접 테이블을 선택해주세요.");
      }
    } catch (error) {
      console.error(error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tableCodeParam, tableIdParam]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    if (!selectedTableId) {
      setSelectedTable((prev) => (isTableLockedByQr ? prev : null));
      return;
    }

    const foundTable = tables.find((table) => String(table.id) === String(selectedTableId));
    setSelectedTable(foundTable || null);
  }, [isTableLockedByQr, selectedTableId, tables]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (currentStep !== STEP_MENU) {
      setIsCartExpanded(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (
      selectedCategoryId &&
      !categories.some((category) => String(category.id) === String(selectedCategoryId))
    ) {
      setSelectedCategoryId("");
    }
  }, [categories, selectedCategoryId]);

  const handleSelectTable = (table) => {
    setSelectedTableGroupId(table.group ? String(table.group) : "");
    setSelectedTableId(String(table.id));
    setSelectedTable(table);
    setTableCodeInput(table.code || "");
    setTableLookupError("");
  };

  const handleFindTableByCode = () => {
    const normalizedCode = tableCodeInput.trim().toLowerCase();
    if (!normalizedCode) {
      setTableLookupError("테이블 코드를 입력해주세요.");
      return;
    }

    const matchedTable = tables.find((table) => table.code?.toLowerCase() === normalizedCode);
    if (!matchedTable) {
      setTableLookupError("일치하는 테이블을 찾지 못했습니다. 코드를 다시 확인해주세요.");
      return;
    }

    handleSelectTable(matchedTable);
  };

  const closeMenuPicker = () => {
    setMenuPicker(null);
    setMenuPickerOptions({});
    setMenuPickerQuantity(1);
  };

  const openMenuPicker = (menu) => {
    if ((menu.option_groups || []).length === 0) {
      setOrderComplete(null);
      setCartItems((prev) => {
        const existingIndex = prev.findIndex((item) => item.menu_id === menu.id && (item.options || []).length === 0);
        if (existingIndex >= 0) {
          const copied = [...prev];
          const target = { ...copied[existingIndex] };
          target.quantity += 1;
          target.line_amount = target.unit_price_snapshot * target.quantity;
          copied[existingIndex] = target;
          return copied;
        }
        return [...prev, buildCartItem(menu, 1, [])];
      });
      setIsCartExpanded(true);
      return;
    }

    setMenuPicker(menu);
    setMenuPickerOptions({});
    setMenuPickerQuantity(1);
  };

  const togglePickerOption = (group, option, checked) => {
    setMenuPickerOptions((prev) => {
      const groupValues = prev[group.id] || [];
      let nextValues = [];

      if (group.max_select <= 1) {
        nextValues = checked ? [option.id] : [];
      } else if (checked) {
        if (groupValues.includes(option.id)) {
          nextValues = groupValues;
        } else {
          if (groupValues.length >= group.max_select) {
            toast.error(`'${group.name}' 옵션은 최대 ${group.max_select}개까지 선택할 수 있습니다.`);
            return prev;
          }
          nextValues = [...groupValues, option.id];
        }
      } else {
        nextValues = groupValues.filter((value) => value !== option.id);
      }

      return {
        ...prev,
        [group.id]: nextValues,
      };
    });
  };

  const pickerTotal = useMemo(() => {
    if (!menuPicker) return 0;
    let optionTotal = 0;
    for (const group of menuPicker.option_groups || []) {
      const selectedIds = menuPickerOptions[group.id] || [];
      for (const option of group.options || []) {
        if (selectedIds.includes(option.id)) optionTotal += option.extra_price;
      }
    }
    return (menuPicker.price + optionTotal) * menuPickerQuantity;
  }, [menuPicker, menuPickerOptions, menuPickerQuantity]);

  const canAddMenuToCart = useMemo(() => {
    if (!menuPicker) return false;
    const groups = menuPicker.option_groups || [];
    let totalSelected = 0;
    for (const group of groups) {
      const selectedIds = menuPickerOptions[group.id] || [];
      const required = group.is_required || group.min_select > 0;
      if (required && selectedIds.length === 0) return false;
      if (selectedIds.length < group.min_select) return false;
      if (selectedIds.length > group.max_select) return false;
      totalSelected += selectedIds.length;
    }
    if (groups.length > 0 && totalSelected === 0) return false;
    return true;
  }, [menuPicker, menuPickerOptions]);

  const addMenuToCart = () => {
    if (!menuPicker) return;

    const selectedOptions = [];
    for (const group of menuPicker.option_groups || []) {
      const selectedIds = menuPickerOptions[group.id] || [];

      if (group.is_required && selectedIds.length === 0) {
        toast.error(`'${group.name}' 옵션은 필수 선택입니다.`);
        return;
      }
      if (selectedIds.length < group.min_select) {
        toast.error(`'${group.name}' 옵션은 최소 ${group.min_select}개를 선택해야 합니다.`);
        return;
      }
      if (selectedIds.length > group.max_select) {
        toast.error(`'${group.name}' 옵션은 최대 ${group.max_select}개까지만 선택할 수 있습니다.`);
        return;
      }

      for (const option of group.options || []) {
        if (selectedIds.includes(option.id)) {
          selectedOptions.push({
            menu_option_id: option.id,
            option_name_snapshot: option.name,
            extra_price_snapshot: option.extra_price,
            quantity: 1,
          });
        }
      }
    }

    appendCartItem(buildCartItem(menuPicker, menuPickerQuantity, selectedOptions));
    closeMenuPicker();
  };

  const updateCartQuantity = (index, nextQuantity) => {
    if (nextQuantity < 1) return;

    setCartItems((prev) => {
      const copied = [...prev];
      const target = copied[index];
      if (!target) return prev;

      const optionTotal = (target.options || []).reduce(
        (sum, option) => sum + option.extra_price_snapshot * (option.quantity || 1),
        0
      );

      target.quantity = nextQuantity;
      target.line_amount = (target.unit_price_snapshot + optionTotal) * nextQuantity;
      return copied;
    });
  };

  const removeCartItem = (index) => {
    setCartItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleMoveToOrderType = () => {
    if (cartItems.length === 0) {
      toast.warning("메뉴를 먼저 선택해주세요.");
      return;
    }

    if (!accessToken) {
      toast.warning("주문을 진행하려면 로그인이 필요해요.");
      return;
    }

    setOrderComplete(null);
    setCurrentStep(STEP_ORDER_TYPE);
  };

  const handleConfirmOrderType = () => {
    if (cartItems.length === 0) {
      toast.warning("메뉴를 먼저 선택해주세요.");
      setCurrentStep(STEP_MENU);
      return;
    }

    if (!orderType) {
      toast.warning("주문 타입을 선택해주세요.");
      return;
    }

    if (orderType === "pickup") {
      setTableLookupError("");
      setCurrentStep(STEP_REVIEW);
      return;
    }

    if (isTableLockedByQr && selectedTableId) {
      setTableLookupError("");
      setCurrentStep(STEP_REVIEW);
      return;
    }

    setCurrentStep(STEP_TABLE);
  };

  const handleMoveToReview = () => {
    if (cartItems.length === 0) {
      toast.warning("메뉴를 먼저 선택해주세요.");
      setCurrentStep(STEP_MENU);
      return;
    }

    if (!selectedTableId) {
      setTableLookupError("서빙 주문은 테이블 선택이 필요합니다.");
      return;
    }

    setCurrentStep(STEP_REVIEW);
  };

  const submitOrder = async () => {
    if (!ensureLoggedIn()) return;

    if (!orderType) {
      setCurrentStep(STEP_ORDER_TYPE);
      toast.warning("주문 타입을 선택해주세요.");
      return;
    }

    if (cartItems.length === 0) {
      setCurrentStep(STEP_MENU);
      toast.error("장바구니가 비어 있습니다.");
      return;
    }

    if (orderType === "serving" && !selectedTableId) {
      setCurrentStep(STEP_TABLE);
      setTableLookupError("서빙 주문은 테이블 선택이 필요합니다.");
      return;
    }

    setSubmitting(true);
    try {
      const submitRes = await tableOrderService.submitOrder({
        table_id: orderType === "serving" ? Number(selectedTableId) : null,
        memo: memo.trim(),
        order_type: orderType,
        items: toSubmitItems(cartItems),
      });

      setOrderComplete({ ...submitRes.data, _itemsSnapshot: cartItems });
      setMemo("");
      setCartItems([]);
      setCurrentStep(STEP_REVIEW);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || "주문 접수에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategoryScroll = (categoryId) => {
    setSelectedCategoryId(categoryId);
    if (!categoryId) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = sectionRefs.current[categoryId];
    if (el) {
      const offset = 116;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const resetAfterOrder = () => {
    setOrderComplete(null);
    setMemo("");
    setCartItems([]);
    setOrderType("");
    setCurrentStep(STEP_MENU);
    setIsCartExpanded(false);
  };

  const handleClearCart = () => {
    setOrderComplete(null);
    setMemo("");
    setCartItems([]);
    setOrderType("");
    setCurrentStep(STEP_MENU);
    setIsCartExpanded(false);
    setTableLookupError("");
  };

  const handleStepBack = (stepId) => {
    if (stepId >= currentStep) return;
    if (stepId === STEP_TABLE && orderType !== "serving") return;
    setCurrentStep(stepId);
  };

  if (loading) {
    return (
      <MobileViewContainer>
        <Container fluid className="py-6">
          <p className="text-center text-gray-500">로딩 중...</p>
        </Container>
      </MobileViewContainer>
    );
  }

  if (loadError) {
    return (
      <MobileViewContainer>
        <Container fluid className="py-6 px-4">
          <div className="flex flex-col items-center gap-4 mt-16 text-center">
            <p className="text-gray-500 text-sm">데이터를 불러오지 못했습니다.</p>
            <button
              className="px-5 py-2 rounded-full bg-stone-800 text-white text-sm font-semibold"
              onClick={() => { setLoadError(false); loadBaseData(); }}
            >
              다시 시도
            </button>
          </div>
        </Container>
      </MobileViewContainer>
    );
  }

  return (
    <MobileViewContainer>
      {/* 고정 헤더: 오더(기본) ↔ 스테퍼(스크롤 시) 크로스페이드 */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-160 z-10 bg-white border-b border-gray-100" style={{ height: "72px" }}>
        {/* 오더 타이틀 */}
        <div className={`absolute inset-0 flex items-center px-5 transition-all duration-300 ${orderComplete ? "justify-center" : "justify-start"} ${(scrolled || currentStep !== STEP_MENU) && !orderComplete ? "opacity-0 -translate-y-1 pointer-events-none" : "opacity-100 translate-y-0"}`}>
          <h1 className="text-xl font-bold mb-0" style={{ fontFamily: "Gmarket-B" }}>{orderComplete ? "주문 완료" : "오더"}</h1>
        </div>
        {/* 스테퍼 (스크롤 시 또는 메뉴 선택 단계가 아닐 때 표시. 단, 주문 완료 시 숨김) */}
        <div className={`absolute inset-0 flex items-center px-4 transition-all duration-300 ${(scrolled || currentStep !== STEP_MENU) && !orderComplete ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"}`}>
          <div className="flex items-center w-full">
            {steps.map((step, idx) => {
              const isSkippedStep = step.id === STEP_TABLE && orderType === "pickup" && currentStep === STEP_REVIEW;
              const isAllComplete = !!orderComplete;
              const isActive = !isAllComplete && step.id === effectiveStep;
              const isReached = isAllComplete || step.id <= effectiveStep || isSkippedStep;
              const isLastStep = idx === steps.length - 1;
              const showCheckCap = isAllComplete && isLastStep;
              const lineReached = isAllComplete || (idx < steps.length - 1 && (steps[idx + 1].id <= effectiveStep || (steps[idx + 1].id === STEP_TABLE && isSkippedStep)));
              return (
                <React.Fragment key={step.id}>
                  <button className="shrink-0 flex items-center leading-none" onClick={() => handleStepBack(step.id)}>
                    {showCheckCap ? (
                      <span className="inline-flex items-center gap-1.5 bg-[#e16036] text-white rounded-full px-2.5 py-1 text-sm font-bold whitespace-nowrap shadow-[0_2px_12px_rgba(225,96,54,0.30)] leading-none">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        주문 완료
                      </span>
                    ) : isActive ? (
                      <span className="inline-flex items-center gap-1.5 text-gray-800 rounded-full px-2.5 py-1 text-sm font-bold whitespace-nowrap transition-all duration-300 shadow-[0_2px_12px_rgba(225,96,54,0.30),0_0_0_3px_rgba(225,96,54,0.08)] leading-none">
                        <span className="relative flex items-center justify-center w-1.5 h-1.5 shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400/70" />
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        </span>
                        {step.title}
                      </span>
                    ) : (
                      <span className={`block w-2 h-2 rounded-full transition-colors duration-300 ${isReached ? "bg-[#e16036]" : "bg-gray-200"}`} />
                    )}
                  </button>
                  {idx < steps.length - 1 && (
                    <div className="flex-1 mx-2" style={{ height: "0.5px", background: lineReached ? "#e16036" : "#e5e7eb", transition: "background 0.3s" }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* 고정 카테고리 바 (메뉴 선택 단계에서만) */}
      {currentStep === STEP_MENU && (
        <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-160 z-9 bg-gray-100 px-4 pt-4 pb-3 flex flex-wrap items-center gap-2" style={{ top: "72px" }}>
          <button
            className={`px-3.5 py-1.5 rounded-full text-sm shadow-xl font-semibold transition ${selectedCategoryId === "" ? "bg-[#e16036] text-white" : "bg-white text-gray-700"}`}
            onClick={() => handleCategoryScroll("")}
          >
            전체
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`px-3.5 py-1.5 rounded-full shadow-lg text-sm font-semibold transition ${String(selectedCategoryId) === String(category.id) ? "bg-[#e16036] text-white" : "bg-white text-gray-700"}`}
              onClick={() => handleCategoryScroll(String(category.id))}
            >
              {category.name}
            </button>
          ))}
        </div>
      )}

      <Container fluid className="bg-gray-100" style={{ paddingTop: currentStep === STEP_MENU ? "140px" : "100px", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "calc(env(safe-area-inset-bottom) + 220px)", minHeight: "100vh" }}>

        {!accessToken && (
          <div className="bg-[#FFF5F0] border border-orange-100 rounded-2xl pl-4 pr-5 py-3 mb-4 flex items-center gap-2.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e16036" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span className="text-sm text-gray-600 flex-1">주문하시려면 로그인이 필요해요.</span>
            <button
              className="flex items-center gap-0.5 text-sm font-semibold text-[#e16036] shrink-0 whitespace-nowrap"
              onClick={() => navigate("/login")}
            >
              로그인
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        )}

        {currentStep === STEP_MENU && (
          <>

            {isTableLockedByQr && selectedTable && (
              <div className="flex justify-end mb-3">
                <span className="text-xs rounded-full px-3 py-1 bg-stone-800 text-white">
                  QR {formatTableSummary(selectedTable)}
                </span>
              </div>
            )}

            <div className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
              {groupedMenus.map(({ category, menus: groupMenus }, groupIdx) => (
                <React.Fragment key={category.id}>
                  {groupIdx > 0 && <div className="h-px bg-gray-100" />}
                  {/* 카테고리 헤더 */}
                  <div ref={(el) => { sectionRefs.current[category.id] = el; }} className="px-4 pt-6 pb-3">
                    <p className="text-lg font-bold text-gray-900 mb-0" style={{ fontFamily: "Gmarket-B" }}>{category.name}</p>
                  </div>
                  {/* 메뉴 아이템 */}
                  {groupMenus.map((menu) => (
                    <React.Fragment key={menu.id}>
                      <div className="h-px bg-gray-100" />
                      <button
                        className={`w-full text-left flex items-center gap-4 px-4 py-5 transition-colors duration-300 ${
                          menu.is_sold_out ? "opacity-50" : "active:bg-[#FFF5F0]"
                        }`}
                        disabled={menu.is_sold_out}
                        onClick={() => openMenuPicker(menu)}
                      >
                        <div className="relative shrink-0 w-18 h-18 rounded-full overflow-hidden">
                          {menu.image ? (
                            <img src={menu.image} alt={menu.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-2xl">
                              🍴
                            </div>
                          )}
                          {menu.is_sold_out && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="text-white text-xs font-bold">품절</span>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-1.5 mb-0.5">
                            <p className="font-bold text-sm text-gray-900 mb-0 truncate" style={{ fontFamily: "Gmarket-M" }}>{menu.name}</p>
                            {(menu.option_groups || []).length > 0 && (
                              <span className="shrink-0 text-xs text-gray-700 bg-gray-200 rounded-full px-1.5 py-0.5">옵션 {menu.option_groups.length}개</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 leading-snug mb-0.5 min-h-3.5 flex flex-wrap">{menu.description || ""}</p>
                          <p className="text-sm font-bold text-gray-900 mb-0">{menu.price.toLocaleString()}원</p>
                        </div>
                        {!menu.is_sold_out && (
                          <div className="shrink-0 w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-lg">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round">
                              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                          </div>
                        )}
                      </button>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              {groupedMenus.length === 0 && <p className="px-4 py-4 text-sm text-gray-500 mb-0">표시할 메뉴가 없습니다.</p>}
            </div>
          </>
        )}

        {currentStep === STEP_ORDER_TYPE && (
          <div className="mb-4">

            <div className="grid grid-cols-1 gap-3">
              {Object.entries(orderTypeLabels).map(([value, label]) => {
                const isSelected = orderType === value;
                const description =
                  value === "serving"
                    ? "선택한 테이블로 주문이 접수됩니다."
                    : "테이블 없이 픽업 주문으로 접수됩니다.";

                return (
                  <button
                    key={value}
                    className={`rounded-2xl p-4 text-left transition ${
                      isSelected
                        ? "bg-[#FFF5F0] border border-[#e16036] shadow-[0_4px_32px_rgba(225,96,54,0.08)]"
                        : "bg-white border border-gray-100 text-gray-800 shadow-[0_2px_24px_rgba(0,0,0,0.05)]"
                    }`}
                    onClick={() => setOrderType(value)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                        isSelected ? "border-[#e16036]" : "border-gray-300"
                      }`}>
                        <div className={`rounded-full transition-all duration-200 ${
                          isSelected ? "w-2 h-2 bg-[#e16036]" : "w-0 h-0"
                        }`} />
                      </div>
                      <div>
                        <div className={`text-base font-bold mb-0.5 ${isSelected ? "text-[#e16036]" : "text-gray-800"}`}>{label}</div>
                        <div className={`text-sm ${isSelected ? "text-gray-600" : "text-gray-500"}`}>{description}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* <div className="mt-3 rounded-2xl bg-white px-4 py-3 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
              <p className="text-xs font-semibold text-gray-400 mb-1.5">주문 요약</p>
                           <p className="text-sm text-gray-600 mb-0.5">메뉴 {cartItems.length}개</p>
              <p className="text-sm font-semibold text-gray-800 mb-0">총 {cartTotal.toLocaleString()}원</p>
            </div> */}

          </div>
        )}

        {currentStep === STEP_TABLE && (
          <div className="mb-4">

            {!selectedTable ? (
              <p className="mb-2 px-1 text-sm font-bold text-[#e16036]">
                주문하시려면 테이블을 먼저 선택해 주세요!
              </p>
            ) : (
              <p className="mb-2 px-1 text-sm font-bold text-green-600 flex items-center gap-1.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                테이블 선택 완료
              </p>
            )}

            <div className="rounded-2xl bg-white px-4 py-3 mb-3 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
              <div className="flex items-center">
                <p className="mb-0 font-bold text-gray-700 flex-1 text-center">테이블 정보</p>
                <div className="flex-1 flex items-center justify-center gap-2">
                  {isTableLockedByQr && selectedTable && (
                    <span className="text-xs font-semibold rounded-full bg-[#e16036] text-white px-3 py-1 shrink-0">QR 확인완료</span>
                  )}
                  {selectedTable ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      {(() => {
                        const group = tableGroups.find((g) => g.id === selectedTable.group);
                        return group?.name ? (
                          <>
                            <span className="text-sm text-gray-700 truncate">{group.name} 구역</span>
                            <span className="text-gray-300">|</span>
                          </>
                        ) : null;
                      })()}
                      <p className="mb-0 font-bold text-gray-900 truncate" style={{ fontFamily: "Gmarket-M" }}>
                        {formatTableSummary(selectedTable)}
                      </p>
                    </div>
                  ) : (
                    <p className="mb-0 text-sm text-gray-500">테이블을 선택해주세요.</p>
                  )}
                </div>
              </div>
            </div>

            {!isTableLockedByQr && (
              <>
                <div className="rounded-2xl bg-white px-4 py-3 mb-3 shadow-[0_2px_20px_rgba(0,0,0,0.04)] flex items-center justify-around">
                  {[
                    { key: false, label: "목록에서 선택" },
                    { key: true,  label: "번호로 직접 입력" },
                  ].map(({ key, label }) => {
                    const isSelected = showTableCodeInput === key;
                    return (
                      <button
                        key={String(key)}
                        type="button"
                        className="flex items-center gap-2"
                        onClick={() => setShowTableCodeInput(key)}
                      >
                        <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${isSelected ? "border-[#e16036]" : "border-gray-300"}`}>
                          <div className={`rounded-full transition-all duration-200 ${isSelected ? "w-2 h-2 bg-[#e16036]" : "w-0 h-0"}`} />
                        </div>
                        <span className={`text-sm font-semibold ${isSelected ? "text-[#e16036]" : "text-gray-600"}`}>{label}</span>
                      </button>
                    );
                  })}
                </div>

                {!showTableCodeInput && (
                  <>
                    <div className="flex flex-wrap gap-2 my-6">
                      <button
                        className={`px-3.5 py-1.5 rounded-full text-sm font-semibold shadow-lg transition ${
                          selectedTableGroupId === "" ? "bg-[#e16036] text-white" : "bg-white text-gray-700"
                        }`}
                        onClick={() => setSelectedTableGroupId("")}
                      >
                        전체
                      </button>
                      {tableGroups.map((group) => (
                        <button
                          key={group.id}
                          className={`flex px-3.5 py-1.5 shadow-lg rounded-full text-sm font-semibold transition ${
                            String(selectedTableGroupId) === String(group.id)
                              ? "bg-[#e16036] text-white"
                              : "bg-white text-gray-700"
                          }`}
                          onClick={() => setSelectedTableGroupId(String(group.id))}
                        >
                          {group.name}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {filteredTables.map((table) => {
                        const isSelected = String(table.id) === String(selectedTableId);
                        return (
                          <button
                            key={table.id}
                            className={`relative rounded-2xl p-2 text-center min-h-21 flex flex-col justify-center transition ${
                              isSelected
                                ? "bg-[#FFF5F0] border border-[#e16036] shadow-[0_2px_24px_rgba(225,96,54,0.10)]"
                                : "bg-white border border-transparent shadow-[0_2px_20px_rgba(0,0,0,0.05)]"
                            }`}
                            onClick={() => {
                              if (String(table.id) === String(selectedTableId)) {
                                setSelectedTableId("");
                                setSelectedTable(null);
                                setTableCodeInput("");
                                setTableLookupError("");
                              } else {
                                handleSelectTable(table);
                              }
                            }}
                          >
                            {isSelected && (
                              <span className="absolute top-1 right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#e16036] text-white">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </span>
                            )}
                            {!selectedTableGroupId && (
                              <span className="text-xs text-gray-700 truncate">{table.group_name || "미지정"}</span>
                            )}
                            <span className={`font-bold text-lg truncate ${selectedTableGroupId ? "" : "mt-1"} ${isSelected ? "text-[#e16036]" : "text-gray-900"}`} style={{ fontFamily: "Gmarket-M" }}>{table.name}</span>
                          </button>
                        );
                      })}
                    </div>

                    {filteredTables.length === 0 && (
                      <p className="text-sm text-gray-500 mt-3 mb-0">선택한 그룹에 표시할 테이블이 없습니다.</p>
                    )}
                  </>
                )}

                {showTableCodeInput && (
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                    <label className="text-sm font-semibold text-gray-900 mb-2 block">테이블 코드</label>
                    <div className="flex gap-2">
                      <input
                      
                        className="flex-1 bg-gray-50 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder-gray-300 border-0 outline-none focus:ring-1 focus:ring-[#e16036] transition"
                        value={tableCodeInput}
                        onChange={(e) => setTableCodeInput(e.target.value)}
                        placeholder="예: T1, SEAT-101"
                      />
                      <button
                        type="button"
                        className="shrink-0 px-4 py-2.5 rounded-xl bg-[#e16036] text-white text-sm font-semibold"
                        onClick={handleFindTableByCode}
                      >
                        확인
                      </button>
                    </div>
                  </div>
                )}

              </>
            )}

            {tableLookupError && <p className="text-sm text-red-500 mt-3 mb-0">{tableLookupError}</p>}
          </div>
        )}

        {currentStep === STEP_REVIEW && (
          <div className="mb-4 space-y-3">

            {orderComplete ? (
              <div className="relative bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)] rounded-t-2xl">
                <div className="p-5 pb-6">
                  <div className="flex flex-col items-center text-center mb-4">
                    <div className="h-14 w-14 rounded-full bg-green-50 flex items-center justify-center mb-2">
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <p className=" text-green-600 font-semibold mb-1">주문이 접수되었습니다</p>
                    <p className="text-sm text-gray-600 mb-0">{new Date(orderComplete.created_at).toLocaleString()}</p>
                  </div>

                  <div className="flex flex-col items-center py-4 border-t-2 border-b-2 border-dashed border-gray-200">
                    <span className="text-xs text-gray-900 mb-1">주문번호</span>
                    <p className="text-3xl font-black text-gray-900 tabular-nums mb-0" style={{ fontFamily: "Gmarket-B" }}>#{orderComplete.id}</p>
                  </div>

                  <div className="pt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-900">주문 타입</span>
                      <span className="font-bold text-gray-900">{orderTypeLabels[orderComplete.order_type] || orderComplete.order_type}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-900">테이블</span>
                      <span className="font-bold text-gray-900">{formatOrderTableSummary(orderComplete)}</span>
                    </div>
                    {(orderComplete._itemsSnapshot || []).length > 0 && (() => {
                      const items = orderComplete._itemsSnapshot;
                      const firstName = items[0].menu_name_snapshot;
                      const summary = items.length === 1
                        ? firstName
                        : `${firstName} 외 ${items.length - 1}건`;
                      return (
                        <div className="flex items-start justify-between text-sm gap-3">
                          <span className="text-gray-900 shrink-0">주문 메뉴</span>
                          <span className="font-bold text-gray-900 text-right truncate">{summary}</span>
                        </div>
                      );
                    })()}
                    {orderComplete.order_type === "pickup" && isTableLockedByQr && selectedTable && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-900">QR 확인 테이블</span>
                        <span className="text-gray-500">{formatTableSummary(selectedTable)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute -left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-gray-100"></div>
                  <div className="absolute -right-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-gray-100"></div>
                  <div className="mx-4 border-t-2 border-dashed border-gray-200"></div>
                </div>

                <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                  <span className="text-base font-bold text-gray-700">총 결제 금액</span>
                  <strong className="text-2xl font-black text-[#e16036] tabular-nums" style={{ fontFamily: "Gmarket-B" }}>{orderComplete.total_amount.toLocaleString()}원</strong>
                </div>

                <svg
                  className="block w-full"
                  height="10"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <polygon points="0,10 0,0 5,10 10,0 15,10 20,0 25,10 30,0 35,10 40,0 45,10 50,0 55,10 60,0 65,10 70,0 75,10 80,0 85,10 90,0 95,10 100,0 100,10" fill="#f3f4f6" />
                </svg>
              </div>
            ) : null}

            {orderComplete && (
              <div className="flex flex-col items-center gap-4 px-4 pt-5 pb-3 text-center">
                <p className="text-sm text-gray-900 mb-0">
                  {orderComplete.order_type === "serving"
                    ? "음식이 준비되면 테이블로 직접 가져다 드릴게요!"
                    : "음식이 준비되면 카운터에서 픽업해 주세요!"}
                </p>
                <p className="text-xs text-gray-900 mb-0">주문 내역은 마이페이지에서 확인 가능합니다.</p>
              </div>
            )}

            {!orderComplete && (
              <>
                <div className="rounded-2xl bg-linear-to-br from-[#fff4ee] to-[#ffe4d6] p-5 border border-gray-100">
                  {orderType === "serving" ? (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-bold text-[#e16036]">서빙 주문</span>
                        {isTableLockedByQr && (
                          <>
                          
                            <span className="text-xs font-semibold rounded-full bg-[#e16036] text-white px-3 py-1 shrink-0">QR 확인완료</span>
                          </>
                        )}
                      </div>
                      <p className="text-xl text-gray-900 font-bold mb-1">
                        {(() => {
                          const group = tableGroups.find((g) => g.id === selectedTable?.group);
                          return (
                            <>
                              <span className="text-[#e16036]">
                                {group?.name ? `${group.name} 구역 | ` : ""}{formatTableSummary(selectedTable)}
                              </span> 로 주문할까요?
                            </>
                          );
                        })()}
                      </p>
                      <p className="text-lg font-semibold text-gray-900 mb-0">주문 내용을 확인해 주세요.</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-bold text-[#e16036]">픽업 주문</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900 leading-snug mb-1">
                        <span className="text-[#e16036]">픽업</span>으로 주문할까요?
                      </p>
                      <p className="text-lg font-semibold text-gray-900 mb-0">주문 내용을 확인해 주세요.</p>
                    </>
                  )}
                </div>

                {orderType === "pickup" && isTableLockedByQr && selectedTable && (
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-[0_2px_20px_rgba(0,0,0,0.04)] border border-gray-100">
                    {/* QR 테이블을 인증했는데, 픽업으로 주문할 경우 */}
                    {/* <p className="text-xs font-semibold text-gray-700 mb-1.5">QR 확인 테이블</p>
                    <p className="text-sm font-bold text-gray-800 mb-0">{formatTableSummary(selectedTable)}</p> */}
                    <p className="text-sm text-gray-900 ">픽업 주문에서는 QR 테이블 정보가 제출에 사용되지 않습니다.</p>
                  </div>
                )}

                <div className="rounded-2xl bg-white overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                  <div className="px-4 pt-3.5 pb-2">
                    <p className="text-lg font-bold text-gray-900 mb-0">주문 리스트</p>
                  </div>
                  {cartItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-8 px-4">
                      <p className="text-base text-gray-900 font-bold  mb-0 text-center">담은 메뉴가 없습니다.</p>
                      <button
                        type="button"
                        className="px-4 py-2 rounded-full border-[#e16036] border text-gray-900 text-sm shadow-sm font-semibold transition"
                        onClick={() => setCurrentStep(STEP_MENU)}
                      >
                        메뉴 담기
                      </button>
                    </div>
                  ) : (
                    <ul className="px-8">
                      {cartItems.map((item, index) => (
                        <li
                          key={`${item.id}-${index}`}
                          className={`py-4 ${index < cartItems.length - 1 ? "border-b border-dashed border-gray-200" : ""}`}
                        >
                          <div className="flex items-start gap-4">
                            {item.image ? (
                              <img src={item.image} alt={item.menu_name_snapshot} className="h-12 w-12 rounded-full object-cover shrink-0" />
                            ) : (
                              <div className="h-12 w-12 rounded-full shrink-0 flex items-center justify-center bg-gray-100 text-gray-400 text-lg">
                                🍴
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <strong className="truncate text-[15px]" style={{ fontFamily: "Gmarket-M" }}>{item.menu_name_snapshot}</strong>
                                <button
                                  className="shrink-0 h-7 w-7 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-gray-100 transition-colors"
                                  aria-label="삭제"
                                  onClick={() => removeCartItem(index)}
                                >
                                  <Trash2 size={14} strokeWidth={1.6} />
                                </button>
                              </div>
                              {(item.options || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {item.options.map((option) => (
                                    <span
                                      key={option.menu_option_id}
                                      className="inline-flex items-center text-[11px] text-gray-700 bg-gray-100 rounded-full px-2 py-0.5"
                                    >
                                      {option.option_name_snapshot}
                                      {option.extra_price_snapshot > 0 && (
                                        <span className="ml-1 text-gray-700 tabular-nums">
                                          (+{option.extra_price_snapshot.toLocaleString()}원)
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {(() => {
                                const optionTotal = (item.options || []).reduce(
                                  (sum, option) => sum + option.extra_price_snapshot * (option.quantity || 1),
                                  0,
                                );
                                const perUnitPrice = item.unit_price_snapshot + optionTotal;
                                return (
                                  <div className="flex items-baseline justify-between gap-2 mt-2">
                                    <span className="text-xs text-gray-700 tabular-nums">
                                      {perUnitPrice.toLocaleString()}원 × {item.quantity}개
                                    </span>
                                    <strong className="text-[16px] font-bold text-gray-900 tabular-nums">{item.line_amount.toLocaleString()}원</strong>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {cartItems.length > 0 && (
                  <div className="bg-white rounded-2xl p-4 ">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1.5">
                      <span>주문 수량</span>
                      <span className="tabular-nums">총 {cartItems.reduce((sum, item) => sum + item.quantity, 0)}개</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2.5">
                      <span>상품 금액</span>
                      <span className="tabular-nums">{cartTotal.toLocaleString()}원</span>
                    </div>
                    <div className="border-t border-dashed border-gray-200 pt-2.5 flex items-center justify-between">
                      <span className="text-base font-bold text-gray-900">합계 금액</span>
                      <strong className="text-xl font-black text-[#e16036] tabular-nums">{cartTotal.toLocaleString()}원</strong>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl p-4 border border-gray-100">
                  <label className="text-base font-bold text-gray-900 mb-2 block">요청 사항</label>
                  <input
                    className="w-full  rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder-gray-600 border border-gray-300 outline-none focus:ring-1 focus:ring-orange-500 transition"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    onBlur={(e) => {
                      if (!e.target.value.trim()) setMemo("");
                    }}
                    placeholder="주방에 전달할 요청사항을 입력하세요."
                  />
                </div>
              </>
            )}
          </div>
        )}

        {menuPicker && (
          <div className="fixed inset-0 bg-black/40 z-999 flex items-center justify-center px-4">
            <div className="bg-white w-full max-w-md rounded-3xl p-4 flex flex-col gap-3">
              <div className="relative aspect-square overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
                {menuPicker.image ? (
                  <img src={menuPicker.image} alt={menuPicker.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </div>
                )}
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                  aria-label="닫기"
                  onClick={closeMenuPicker}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <h3 className="text-xl font-bold px-3 text-gray-900 mb-0" style={{ fontFamily: "Gmarket-B" }}>
                {menuPicker.name}
              </h3>
              {menuPicker.description ? (
                <p className="text-sm text-gray-500 mb-0">{menuPicker.description}</p>
              ) : null}

              <div className="flex items-center justify-between p-3 border-b border-gray-100">
                <span className="text-lg text-gray-900 font-bold">가격</span>
                <span className="text-lg font-bold text-gray-900 tabular-nums">{menuPicker.price.toLocaleString()}원</span>
              </div>

              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {(menuPicker.option_groups || []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                    선택해야 하는 옵션이 없습니다. 수량만 정한 뒤 바로 장바구니에 담을 수 있습니다.
                  </div>
                ) : null}
                {(menuPicker.option_groups || []).map((group) => {
                  const selected = menuPickerOptions[group.id] || [];
                  const required = group.is_required || group.min_select > 0;
                  return (
                    <div key={group.id} className="border rounded-2xl p-3">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-base font-bold text-gray-900">{group.name}</span>
                        {required && (
                          <span className="text-xs text-red-500 bg-red-50 rounded-full px-1.5 py-0.5">필수</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-700 mb-2">{formatGroupRule(group)}</div>
                      {(group.options || []).map((option) => {
                        const checked = selected.includes(option.id);
                        const isSingleSelect = group.max_select <= 1;
                        return (
                          <label key={option.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer">
                            {isSingleSelect ? (
                              <input
                                type="radio"
                                name={`option-group-${group.id}`}
                                checked={checked}
                                className="form-radio h-4 w-4 text-[#e16036] accent-[#e16036]"
                                onChange={(e) => togglePickerOption(group, option, e.target.checked)}
                              />
                            ) : (
                              <input
                                type="checkbox"
                                checked={checked}
                                className="form-checkbox h-4 w-4 text-black"
                                onChange={(e) => togglePickerOption(group, option, e.target.checked)}
                              />
                            )}
                            <span className="text-sm text-gray-800 flex-1 truncate">{option.name}</span>
                            <span className="text-sm tabular-nums ml-auto shrink-0 font-semibold text-gray-700">
                              +{option.extra_price.toLocaleString()}원
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-3">
                <div className="shrink-0">
                  <QuantityButtonInput
                    variant="subtle"
                    quantity={menuPickerQuantity}
                    detailPage={true}
                    onChange={(value) => {
                      if (value < 1) return;
                      setMenuPickerQuantity(value);
                    }}
                  />
                </div>
                <button
                  type="button"
                  disabled={!canAddMenuToCart}
                  onClick={addMenuToCart}
                  className={`flex-1 rounded-xl py-3 text-base font-bold transition ${
                    canAddMenuToCart
                      ? "bg-[#e16036] text-white shadow-[0_4px_16px_rgba(225,96,54,0.25)] active:scale-[0.99]"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {pickerTotal.toLocaleString()}원 담기
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>

      <div className="fixed bottom-19.5 left-1/2 -translate-x-1/2 w-full max-w-160 px-5 z-997 ">
        <div className=" bg-white rounded-3xl px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
          {cartItems.length > 0 && currentStep !== STEP_REVIEW && (
            <div className=" flex flex-col gap-2 mb-2 border-1.5 border-gray-200 pb-2">
              <button
                type="button"
                className="w-full flex justify-center mb-1.5"
                onClick={() => setIsCartExpanded((prev) => !prev)}
              >
                <div className="w-12 h-0.75 rounded-full bg-gray-300" />
              </button>
              <div className="flex items-center px-2 justify-between gap-3">
                <button
                  type="button"
                  className="text-left flex-1"
                  onClick={() => setIsCartExpanded((prev) => !prev)}
                >
                  <p className="mb-0 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                    <FiShoppingCart className="shrink-0 text-base" />
                    <span>장바구니</span>
                    <span className="inline-flex min-w-4.5 items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-xs font-bold leading-none text-white">
                      {cartItems.length}
                    </span>
                  </p>
                </button>
                <button className="text-xs text-gray-700" onClick={handleClearCart}>비우기</button>
              </div>

              {isCartExpanded && (
                <div className="flex flex-col gap-2 mt-2 space-y-2 max-h-[32vh] overflow-y-auto overscroll-contain">
                  {cartItems.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="bg-white border border-gray-200 rounded-2xl px-3 py-2.5">
                      <div className="flex items-start gap-4">
                        {item.image ? (
                          <img src={item.image} alt={item.menu_name_snapshot} className="h-10 w-10 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded-full shrink-0 flex items-center justify-center bg-gray-100 text-gray-400 text-base">
                            🍴
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <strong className="truncate text-sm" style={{ fontFamily: "Gmarket-M" }}>{item.menu_name_snapshot}</strong>
                            <button
                              className="shrink-0 h-6 w-6 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-gray-100 transition-colors ml-auto"
                              aria-label="삭제"
                              onClick={() => removeCartItem(index)}
                            >
                              <Trash2 size={12} strokeWidth={1.6} />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 truncate mb-0">
                            {(item.options || [])
                              .map((option) => `${option.option_name_snapshot}(+${option.extra_price_snapshot})`)
                              .join(", ") || "옵션 없음"}
                          </p>
                          <div className="flex items-center justify-between gap-3 mt-1.5">
                            <strong className="text-sm tabular-nums">{item.line_amount.toLocaleString()}원</strong>
                            <QuantityButtonInput
                              quantity={item.quantity}
                              detailPage={true}
                              variant="subtle"
                              size={22}
                              onChange={(value) => updateCartQuantity(index, value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {currentStep === STEP_REVIEW && orderComplete ? (
            <div className="flex flex-col gap-2 mb-4">
              <button
                type="button"
                onClick={() => navigate(`/my-page/table-order/${orderComplete.id}`)}
                className="w-full rounded-xl bg-[#e16036] hover:bg-[#cc5530] text-white py-3.5 px-4 text-base font-bold transition-colors shadow-[0_4px_14px_rgba(225,96,54,0.25)]"
              >
                주문 현황 확인하기
              </button>
              <button
                type="button"
                onClick={resetAfterOrder}
                className="w-full rounded-xl bg-white border-2 border-[#e16036] text-[#e16036] hover:bg-[#fff4ee] py-3 px-4 text-sm font-bold transition-colors"
              >
                새 주문 시작
              </button>
            </div>
          ) : (
            <div className="flex items-center px-2 justify-between gap-3">
              <div className="flex flex-col justify-center">
                {currentStep === STEP_REVIEW && cartItems.length === 0 ? (
                  <p className=" mb-0 text-base font-bold text-gray-900 leading-none">장바구니가 비었어요</p>
                ) : (
                  <p className="mb-0 text-xl font-black text-gray-950 leading-none">총 {cartTotal.toLocaleString()}원</p>
                )}
              </div>

              {currentStep === STEP_MENU && (
                <Button type="button" variant="primary" size="sm" className="rounded-xl" onClick={handleMoveToOrderType}>
                  주문 타입 선택
                </Button>
              )}
              {currentStep === STEP_ORDER_TYPE && (
                <Button type="button" variant="primary" size="sm" className="rounded-xl" onClick={handleConfirmOrderType}>
                  테이블 선택하기
                </Button>
              )}
              {currentStep === STEP_TABLE && (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="rounded-xl"
                  onClick={handleMoveToReview}
                  disabled={!selectedTable}
                >
                  {selectedTable ? `${formatTableSummary(selectedTable)} 테이블로 진행` : "테이블을 선택해 주세요"}
                </Button>
              )}
              {currentStep === STEP_REVIEW && !orderComplete && (
                <Button type="button" variant="primary" size="sm" className="rounded-xl" onClick={submitOrder} disabled={submitting || cartItems.length === 0}>
                  {submitting ? "주문 접수 중..." : "이대로 주문하기"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <LoginPromptModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <NavBottomBar activeBar={3} />
    </MobileViewContainer>
  );
};

export default CustomerOrder;
